// Compose and send a document to the client.
//
// The subject and body are generated by the server so the wording is translated
// consistently and lives in one place; the user edits them before sending.

import { useState, type FormEvent } from 'react';
import { api, type EmailDefaults } from '../lib/api';
import { fieldErrors, useAsync, useErrorMessage } from '../lib/hooks';
import { useI18n, useT } from '../lib/i18n';
import { useToast } from './Toast';
import { Icon } from './Icon';
import { ErrorAlert, LoadingRow, Modal, TextAreaField, TextField } from './ui';

export interface EmailDialogProps {
  kind: 'invoice' | 'quotation';
  id: number;
  /** Shown as the attachment chip; purely informational. */
  documentLabel: string;
  onClose: () => void;
  onSent?: () => void;
}

export function EmailDialog({ kind, id, documentLabel, onClose, onSent }: EmailDialogProps) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const toMessage = useErrorMessage();

  const defaults = useAsync<EmailDefaults>(
    () => (kind === 'invoice'
      ? api.invoiceEmailDefaults(id, locale)
      : api.quotationEmailDefaults(id, locale)),
    [kind, id, locale]);

  if (defaults.loading && !defaults.data) {
    return <Modal title={t('email.title')} onClose={onClose}><LoadingRow /></Modal>;
  }
  if (defaults.error || !defaults.data) {
    return (
      <Modal title={t('email.title')} onClose={onClose}>
        <ErrorAlert message={defaults.error ?? t('error.unexpected')} onRetry={defaults.reload} />
      </Modal>
    );
  }

  return (
    <ComposeForm
      kind={kind}
      id={id}
      documentLabel={documentLabel}
      defaults={defaults.data}
      onClose={onClose}
      onSent={(to) => {
        toast.success(t('email.sent', { to }));
        onSent?.();
        onClose();
      }}
      toMessage={toMessage}
    />
  );
}

function ComposeForm({ kind, id, documentLabel, defaults, onClose, onSent, toMessage }: {
  kind: 'invoice' | 'quotation';
  id: number;
  documentLabel: string;
  defaults: EmailDefaults;
  onClose: () => void;
  onSent: (to: string) => void;
  toMessage: (err: unknown) => string;
}) {
  const t = useT();
  const [to, setTo] = useState(defaults.to);
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(defaults.subject);
  const [message, setMessage] = useState(defaults.message);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [sending, setSending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSending(true);
    setErrors({});
    setFormError(undefined);
    const payload = { to: to.trim(), cc: cc.trim(), subject: subject.trim(), message };
    try {
      if (kind === 'invoice') await api.emailInvoice(id, payload);
      else await api.emailQuotation(id, payload);
      onSent(payload.to);
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal
      title={t('email.title')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={sending}>
            {t('common.cancel')}
          </button>
          <button type="submit" form="email-form" className="btn btn--primary" disabled={sending || !defaults.enabled}>
            {sending ? <span className="spinner" /> : <Icon name="send" />}
            {sending ? t('email.sending') : t('email.sendButton')}
          </button>
        </>
      }
    >
      <form id="email-form" onSubmit={onSubmit} noValidate className="stack">
        {!defaults.enabled && <ErrorAlert message={t('email.notConfigured')} />}
        {formError && <ErrorAlert message={formError} />}
        {defaults.enabled && !defaults.to && (
          <div className="alert alert--warning">
            <Icon name="alert-circle" />
            <div>{t('email.noClientAddress')}</div>
          </div>
        )}

        <TextField label={t('email.to')} type="email" required autoFocus={!defaults.to}
          value={to} error={errors.to} onChange={(e) => setTo(e.target.value)} />
        <TextField label={t('email.cc')} type="email" optional
          value={cc} error={errors.cc} onChange={(e) => setCc(e.target.value)} />
        <TextField label={t('email.subject')} required
          value={subject} error={errors.subject} onChange={(e) => setSubject(e.target.value)} />
        <TextAreaField label={t('email.message')} rows={10}
          value={message} error={errors.message} onChange={(e) => setMessage(e.target.value)} />

        <div className="field">
          <span className="field__label">{t('email.attachment')}</span>
          <div className="row">
            <span className="badge badge--accent">
              <Icon name="file-text" size={13} />
              {defaults.filename}
            </span>
            <span className="subtle">{documentLabel}</span>
          </div>
        </div>
      </form>
    </Modal>
  );
}
